import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UserPreferencesRequest } from '@/api';
import { useInputText } from '@/hooks';
import { profileActions } from '@/redux/actions';
import { profileSelectors } from '@/redux/selectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

import { Validation } from '@/models';

import { Input, InputGroup, Button, AlertBox } from '@/components';

import { ModalInfo, ModalInfoProps } from '../ModalInfo';

interface WebhookModalProps extends ModalInfoProps {}

const WebhookModal = ({ isOpen, onClose }: WebhookModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    data: profileData,
    loading,
    loadingPreferences,
    error,
  } = useAppSelector(profileSelectors.getProfileSlice);
  const webhookUrl = useAppSelector(profileSelectors.webhookUrlSetting);
  const WEBHOOK_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'regex',
        value:
          /^https:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/,
        message: t('translation.field_is_not_url'),
      },
    ],
    [t],
  );

  const webHookField = useInputText(webhookUrl || '', WEBHOOK_VALIDATIONS);

  const submitHandler = () => {
    if (
      !loading &&
      !loadingPreferences &&
      webHookField.isValid &&
      webHookField
    ) {
      const payload: UserPreferencesRequest = {
        preferences: {
          webhook: {
            url: webHookField.value,
          },
        },
        url: profileData?.webhookUrl,
      };
      dispatch(
        profileActions.updatePreferences({
          payload,
          callback: onClose,
        }),
      );
    }
  };

  const clearHandler = () => {
    error && dispatch(profileActions.setError(null));
    webHookField.onChange('');
  };

  const cancelHandler = () => {
    error && dispatch(profileActions.setError(null));
    onClose();
  };

  const closeHandler = cancelHandler;

  const handleChange = (value: string) => {
    error && dispatch(profileActions.setError(null));
    webHookField.onChange(value);
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.webhook_label')}
      actions={
        <>
          <Button
            type="primary"
            disabled={loading || loadingPreferences}
            isLoading={loadingPreferences}
            corner="rect"
            onClick={submitHandler}
          >
            {t('translation.save_changes_btn')}
          </Button>
          <Button
            type="secondary"
            disabled={loading || loadingPreferences}
            corner="rect"
            onClick={clearHandler}
          >
            {t('translation.btn_clear')}
          </Button>
          <Button
            type="secondary"
            disabled={loading || loadingPreferences}
            corner="rect"
            onClick={cancelHandler}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
      onClose={closeHandler}
    >
      <InputGroup label={t('translation.monitoring_webhook_field')}>
        <Input
          id="webhook"
          name="webhook"
          type="text"
          value={webHookField.value as string}
          onChange={handleChange}
          onBlur={webHookField.onBlur}
          disabled={loading}
          isError={webHookField.isDirty && !webHookField.isValid}
        />
        {webHookField.isDirty && !webHookField.isValid && (
          <AlertBox type="danger" small>
            {{ description: webHookField.errors[0] }}
          </AlertBox>
        )}
        {error && (
          <AlertBox type="danger" small>
            {{ description: error }}
          </AlertBox>
        )}
      </InputGroup>
    </ModalInfo>
  );
};

export default WebhookModal;
